import { clsx, type ClassValue } from 'clsx'
import { extendTailwindMerge } from 'tailwind-merge'

const dsBorderWidths = ['ds-none', 'ds-s', 'ds-m', 'ds-l']
const isDsToken = (value: string) => value.startsWith('ds-')

// tailwind-merge does not read @theme. Register custom scales so overrides merge
// correctly without confusing font sizes/weights/shadows with colors/families.
const twMerge = extendTailwindMerge({
  extend: {
    theme: {
      text: [
        ...[10, 11, 12, 13, 14, 16, 18, 20, 24, 32, 40].map(
          (size) => `ds-${size}`
        ),
        ...[14, 16, 18, 20, 24, 32].map((size) => `title-${size}`),
        ...[12, 14, 16, 18].map((size) => `body-${size}`),
        ...[10, 11, 12, 13, 14, 16, 18, 20].flatMap((size) => [
          `interface-default-${size}`,
          `interface-strong-${size}`,
        ]),
        ...[11, 12, 14, 16].map((size) => `interface-link-${size}`),
      ],
      'font-weight': ['ds-regular', 'ds-medium', 'ds-semibold', 'ds-bold'],
      spacing: [
        ...[0, 2, 4, 8, 12, 16, 24, 32, 48, 64, 96].map((size) => `ds-${size}`),
        ...['xs', 's', 'm', 'l', 'xl', 'xxl', 'xxxl'].map(
          (size) => `ds-icon-${size}`
        ),
        ...['xs', 's', 'm', 'l', 'xl', 'xxl', 'xxxl'].map(
          (size) => `ds-control-${size}`
        ),
      ],
      radius: ['ds-none', 'ds-xs', 'ds-s', 'ds-m', 'ds-l', 'ds-pill'],
      leading: ['ds-tight', 'ds-snug', 'ds-normal'],
      container: ['ds-xs', 'ds-s', 'ds-m', 'ds-l', 'ds-xl', 'ds-2xl'],
      shadow: [
        ...['xs', 's', 'm', 'l', 'xl', '2xl', 's-blur', 'm-blur', 'l-blur'].map(
          (size) => `elevation-${size}`
        ),
        'focus-ring-default',
        'focus-ring-subtle',
        'focus-ring-danger',
      ],
    },
    classGroups: {
      opacity: [{ opacity: ['ds-disabled'] }],
      // Unregistered, border-ds-s reads as a border colour and drops either the
      // width or the colour it sits next to.
      'border-w': [{ border: dsBorderWidths }],
      'border-w-x': [{ 'border-x': dsBorderWidths }],
      'border-w-y': [{ 'border-y': dsBorderWidths }],
      'border-w-s': [{ 'border-s': dsBorderWidths }],
      'border-w-e': [{ 'border-e': dsBorderWidths }],
      'border-w-t': [{ 'border-t': dsBorderWidths }],
      'border-w-r': [{ 'border-r': dsBorderWidths }],
      'border-w-b': [{ 'border-b': dsBorderWidths }],
      'border-w-l': [{ 'border-l': dsBorderWidths }],
      // bg-hover-ds-* / bg-pressed-ds-* from index.css set background-color.
      'bg-color': [{ 'bg-hover': [isDsToken], 'bg-pressed': [isDsToken] }],
      // text-hover-ds-* from index.css sets color.
      'text-color': [{ 'text-hover': [isDsToken] }],
    },
  },
})

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}
